import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FleetFilterOptions, FleetFilters, Vehicle } from '../../../types/Vehicle';
import equipmentFleetService from '../../../services/equipmentFleetService';
import EquipmentTable from '../components/EquipmentTable';
import EquipmentFilters, { ALL_FLEET_STATUSES, DEFAULT_FLEET_STATUS, FLEET_CHIPS, FLEET_FILTER_KEYS } from '../components/EquipmentFilters';
import EquipmentBulkBar from '../components/EquipmentBulkBar';
import EquipmentSubnav from '../components/EquipmentSubnav';
import PendingTransfersPanel from '../components/PendingTransfersPanel';
import type { PendingTransferRow } from '../../../services/vehicleTransferService';
import { exportEquipmentCsv } from '../utils/exportEquipmentCsv';
import { EQUIPMENT_PERMISSIONS } from '../utils/equipmentPermissions';
import ServerPagination from '../../../components/ServerPagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { FilterChips, PageHeader, ListPage } from '@/components/ui';

const EMPTY_FILTER_OPTIONS: FleetFilterOptions = {
  currentStatuses: [],
  serviceAreas: [],
  vehicleClassifications: [],
  currentYards: [],
  vehicleTypes: [],
  foremanAreas: [],
  myForemanAreaIds: []
};

export function filtersFromParams(searchParams: URLSearchParams): FleetFilters {
  const next: FleetFilters = {};
  FLEET_FILTER_KEYS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) next[key] = value;
  });
  // V3-026: no status in the URL is the default (not Inactive or Retired); "all" is every status.
  if (!searchParams.get('fleetStatus')) next.fleetStatus = DEFAULT_FLEET_STATUS;
  else if (searchParams.get('fleetStatus') === ALL_FLEET_STATUSES) delete next.fleetStatus;
  return next;
}

const DEFAULT_PAGE_SIZE = 20;

/**
 * The Fleet list. The URL is the one source of the filters and page, so the list fetches once per
 * change (EQ-29): there's no second copy of the filters in state to re-sync after navigation.
 */
const EquipmentPage: React.FC = () => {
  useDocumentTitle('Equipment');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlKey = searchParams.toString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filters = useMemo(() => filtersFromParams(searchParams), [urlKey]);
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const pageSize = Math.max(1, parseInt(searchParams.get('pageSize') || String(DEFAULT_PAGE_SIZE), 10) || DEFAULT_PAGE_SIZE);

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [pendingTransfers, setPendingTransfers] = useState<PendingTransferRow<Vehicle>[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [totals, setTotals] = useState({ totalCount: 0, totalPages: 0 });
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [reloadToken, setReloadToken] = useState(0);
  const [filterOptions, setFilterOptions] = useState<FleetFilterOptions>(EMPTY_FILTER_OPTIONS);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const { hasPermission } = usePermissions();
  const canCreateVehicle = hasPermission(EQUIPMENT_PERMISSIONS.vehicleCreate);
  const canSetAvailability = hasPermission(EQUIPMENT_PERMISSIONS.availabilitySet);
  const canTransferForemanArea = hasPermission(EQUIPMENT_PERMISSIONS.transferForemanArea);

  // One request per distinct query (EQ-29).
  const queryKey = JSON.stringify({ page, pageSize, filters, sortBy, sortDir, reloadToken });
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    equipmentFleetService
      .getFleet(page, pageSize, { ...filters, pinTransfers: '1', ...(sortBy ? { sortBy, sortOrder: sortDir } : {}) })
      .then((response) => {
        if (cancelled) return;
        setVehicles(response.data);
        setPendingTransfers((response.pendingTransfers as PendingTransferRow<Vehicle>[] | undefined) ?? []);
        setTotals({ totalCount: response.pagination.totalCount, totalPages: response.pagination.totalPages });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error fetching equipment:', error);
        setVehicles([]);
        setLoadError(error instanceof Error ? error.message : 'Failed to load equipment.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  useEffect(() => {
    equipmentFleetService
      .getFilterOptions()
      .then(setFilterOptions)
      .catch((error) => console.error('Error fetching equipment filter options:', error));
  }, []);

  const updateUrl = (changes: { page?: number; pageSize?: number; filters?: FleetFilters }) => {
    const params = new URLSearchParams(searchParams);
    if (changes.page) params.set('page', String(changes.page));
    if (changes.pageSize) params.set('pageSize', String(changes.pageSize));
    if (changes.filters) {
      FLEET_FILTER_KEYS.forEach((key) => {
        const value = changes.filters?.[key];
        if (key === 'fleetStatus') {
          // The default stays out of the URL; an empty choice means every status.
          if (value === DEFAULT_FLEET_STATUS) params.delete(key);
          else params.set(key, value || ALL_FLEET_STATUSES);
          return;
        }
        if (value) params.set(key, value);
        else params.delete(key);
      });
    }
    setSearchParams(params);
  };

  const handleSort = (key: string, dir: 'asc' | 'desc') => {
    setSortBy(key);
    setSortDir(dir);
    if (page !== 1) updateUrl({ page: 1 });
  };

  const handleFilterChange = (newFilters: FleetFilters) => {
    setSelectedIds(new Set());
    updateUrl({ page: 1, filters: newFilters });
  };

  const handleExportCsv = async () => {
    try {
      setExportError(null);
      setExporting(true);
      const response = await equipmentFleetService.getFleet(1, Math.max(totals.totalCount, 1), filters);
      exportEquipmentCsv(response.data);
    } catch (error) {
      console.error('Error exporting equipment CSV:', error);
      setExportError(error instanceof Error ? error.message : 'Failed to export CSV.');
    } finally {
      setExporting(false);
    }
  };

  const chips = FLEET_CHIPS.filter((chip) => Boolean(filters[chip.key])).map((chip) => ({
    key: chip.key,
    label: chip.label,
    onClear: () => handleFilterChange({ ...filters, [chip.key]: '' })
  }));

  return (
    // V3-032: the ONE list-page layout, as on Vendors: the table is a white card and the page's
    // one scroll box, so its header stays put while the list scrolls (TBL-02, TBL-05).
    <ListPage>
      <PageHeader
        title="Equipment"
        actions={[
          {
            key: 'export',
            label: exporting ? 'Exporting...' : 'Export CSV',
            icon: 'bi-download',
            intent: 'neutral',
            disabled: exporting || totals.totalCount === 0,
            onClick: handleExportCsv,
          },
          {
            key: 'add',
            label: 'Add Unit',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canCreateVehicle,
            onClick: () => navigate('/vehicles/new'),
          },
        ]}
      />

      <EquipmentSubnav />

      {loadError && (
        <div className="tw-mb-3 tw-rounded-md tw-border tw-border-danger tw-bg-danger/10 tw-px-3 tw-py-2 tw-text-sm tw-text-danger">
          {loadError}
        </div>
      )}
      {exportError && (
        <div className="tw-mb-3 tw-rounded-md tw-border tw-border-danger tw-bg-danger/10 tw-px-3 tw-py-2 tw-text-sm tw-text-danger">
          {exportError}
        </div>
      )}

      <EquipmentFilters filters={filters} filterOptions={filterOptions} onFilterChange={handleFilterChange} />

      <FilterChips chips={chips} data-testid="equipment-filter-chips" />

      <EquipmentBulkBar
        selected={vehicles.filter((v) => selectedIds.has(v.id))}
        foremanAreas={filterOptions.foremanAreas || []}
        canSetAvailability={canSetAvailability}
        canTransferForemanArea={canTransferForemanArea}
        onClear={() => setSelectedIds(new Set())}
        onApplied={async () => setReloadToken((n) => n + 1)}
      />

      {page === 1 && pendingTransfers.length > 0 && (
        <PendingTransfersPanel
          rows={pendingTransfers}
          onOpenUnit={(vehicleId) => navigate(`/equipment/${vehicleId}`)}
          onOpenTransfer={(transferId) => navigate(`/equipment/transfers/${transferId}`)}
        />
      )}

      {filters.pendingTransfer !== '1' && (
        <div className="tw-mb-2 tw-text-sm tw-text-content-muted">
          {totals.totalCount.toLocaleString('en-CA')} {totals.totalCount === 1 ? 'unit' : 'units'}
          {pendingTransfers.length > 0 ? ' more' : ''}
        </div>
      )}

      {filters.pendingTransfer === '1' ? (
        !loading && pendingTransfers.length === 0 && (
          <p className="tw-text-sm tw-text-content-muted">No unit has a pending transfer.</p>
        )
      ) : (
        <>
          <ListPage.Body aria-busy={loading}>
          <EquipmentTable
            vehicles={vehicles}
            loading={loading}
            selectedIds={selectedIds}
            onToggleRow={(id) => {
              setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              });
            }}
            onToggleAll={(checked) => {
              setSelectedIds(checked ? new Set(vehicles.map((v) => v.id)) : new Set());
            }}
            onSelectVehicle={(vehicle) => navigate(`/equipment/${vehicle.id}`)}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
          />
          </ListPage.Body>
          <ServerPagination
            currentPage={page}
            totalPages={totals.totalPages}
            totalCount={totals.totalCount}
            pageSize={pageSize}
            onPageChange={(next) => {
              setSelectedIds(new Set());
              updateUrl({ page: next });
            }}
            onPageSizeChange={(next) => {
              setSelectedIds(new Set());
              updateUrl({ page: 1, pageSize: next });
            }}
          />
        </>
      )}
    </ListPage>
  );
};

export default EquipmentPage;
